import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import hospitalsServices from "../../../../services/hospitalsServices";
import { PenLine, Plus, Search, Trash2 } from "lucide-react";
import DataTable from "../../../../widgets/DataTable";
import "./UnitsView.scss";
import CustomizableModal from "../../../../widgets/Modal/CustomizableModal";
import CustomSnackbar from "../../Snackbar/SnackBar";

export default function UnitsView() {
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [hospitalsNames, setHospitalsNames] = useState<any[]>([]);
  const [visibleUnits, setVisibleUnits] = useState<Array<any>>([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUnits, setSelectedUnits] = useState<any[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState<any | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMode, setSnackbarMode] = useState("");
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [unitsTableData, setUnitsTableData] = useState<Array<any>>([]);

  const handleSelectedUnitsChange = (selected: any[]) => {
    setSelectedUnits(selected);
  };

  const unitsHeaders = [
    {
      name: "Nombre",
      id: "hospital_name",
    },
  ];

  const fetchHospitals = () => {
    hospitalsServices
      .getAllActiveHospitals()
      .then((res) => {
        setHospitals(res);
        const hospitalNames = res.map((unit: any) => unit.hospital_name);
        setHospitalsNames(hospitalNames);
      })
      .catch((err) => {
        console.error("Error al obtener los cambios de la idea", err);
        setHospitals([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  useEffect(() => {
    if (visibleUnits.length > 0) {
      const updatedUnits = visibleUnits.map((unit) => ({
        ...unit,
        actions: [
          <IconButton
            key="edit"
            onClick={() => {
              setUnitToEdit(unit);
              setShowEditModal(true);
            }}
            disableRipple
          >
            <PenLine className="menu-actions__edit-icon" />
          </IconButton>,
        ],
      }));
      setUnitsTableData(updatedUnits);
    }
  }, [visibleUnits]);

  useEffect(() => {
    const filtered = hospitals.filter((unit) => {
      const matchesUnits =
        !searchInput?.trim() ||
        unit.hospital_name.toLowerCase().includes(searchInput.toLowerCase());

      return matchesUnits;
    });
    setVisibleUnits(filtered);
  }, [searchInput, hospitals]);

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flex: "9" }} className="header__searching__search-bar">
          <Box>
            <Search className="header__searching__search-bar__search-icon" />
          </Box>
          <Autocomplete
            freeSolo
            forcePopupIcon={false}
            fullWidth
            options={hospitalsNames}
            inputValue={searchInput}
            onInputChange={(e, newInput, reason) => {
              if (reason === "clear") {
                setSearchInput("");
              } else {
                setSearchInput(newInput);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar" />
            )}
          />
        </Box>
        <Box className="menu-actions">
          <IconButton onClick={() => setShowAddModal(true)} disableRipple>
            <Plus className="menu-actions__add-icon" />
          </IconButton>
          <IconButton
            disabled={selectedUnits?.length === 0}
            onClick={() => setShowDeleteModal(true)}
            disableRipple
          >
            <Trash2 className="menu-actions__trash-icon" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ marginTop: "20px" }}>
        <DataTable
          rows={unitsTableData}
          headers={unitsHeaders}
          isLoading={isLoading}
          tableType="checkbox"
          onSelectionChange={handleSelectedUnitsChange}
        />
      </Box>
      <CustomizableModal
        isOpen={showAddModal}
        nextButton="Guardar"
        modalType="add"
        options={hospitalsNames}
        panelView="unit"
        cancel={() => setShowAddModal(false)}
        save={(value) => {
          if (value?.trim()) {
            setShowAddModal(false);
            setIsModalLoading(true);
            hospitalsServices
              .createHospital({ name: value })
              .then(() => {
                setIsModalLoading(false);
                fetchHospitals();
              })
              .finally(() => {
                setSnackbarMode("add");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showEditModal}
        initialValue={unitToEdit?.hospital_name}
        nextButton="Guardar"
        modalType="edit"
        options={hospitalsNames}
        panelView="unit"
        cancel={() => setShowEditModal(false)}
        save={(newName) => {
          if (unitToEdit && newName?.trim()) {
            setShowEditModal(false);
            setIsModalLoading(true);
            hospitalsServices
              .editHospital(unitToEdit.id, { name: newName })
              .then(() => {
                setUnitToEdit(null);
                setIsModalLoading(false);
                fetchHospitals();
              })
              .finally(() => {
                setSnackbarMode("edit");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomizableModal
        isOpen={showDeleteModal}
        nextButton="Sí, eliminar"
        modalType="delete"
        panelView="unit"
        cancel={() => setShowDeleteModal(false)}
        save={() => {
          if (selectedUnits?.length > 0) {
            setShowDeleteModal(false);
            setIsModalLoading(true);
            Promise.all(
              selectedUnits.map((unit) =>
                hospitalsServices.deactivateHospital(unit.id)
              )
            )
              .then(() => {
                setSelectedUnits([]);
                setIsModalLoading(false);
                fetchHospitals();
              })
              .finally(() => {
                setSnackbarMode("delete");
                setOpenSnackbar(true);
              });
          }
        }}
      />
      <CustomSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        menu="units"
        mode={snackbarMode}
      />
      {isModalLoading && (
        <Box className="loader-overlay">
          <Box className="loader-container">
            <Box className="custom-loader" />
          </Box>
        </Box>
      )}
    </Box>
  );
}
